import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [activeOrg, setActiveOrg] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();

  // Load user and active org on mount (only once)
  useEffect(() => {
    // Prevent multiple initializations
    if (initialized) return;
    
    const initAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const currentUser = await authService.getCurrentUser();
          if (currentUser) {
            setUser(currentUser);
            
            // Load organizations
            const orgs = await authService.getOrganizations();
            setOrganizations(orgs);
            
            // Load active organization from localStorage
            const activeOrgId = localStorage.getItem('activeOrgId');
            // Check if there's a stored activeOrg with updated data
            const storedActiveOrg = localStorage.getItem('activeOrg');
            let orgToUse = null;
            
            if (storedActiveOrg) {
              try {
                const parsedOrg = JSON.parse(storedActiveOrg);
                // Use stored org if it matches an org from the API (prefer API data)
                const apiOrg = orgs.find(o => o.id === parsedOrg.id);
                orgToUse = apiOrg || parsedOrg;
              } catch (e) {
                console.error('Failed to parse stored activeOrg:', e);
              }
            }
            
            if (activeOrgId && orgs.length > 0) {
              const org = orgToUse || orgs.find(o => o.id === activeOrgId);
              if (org) {
                setActiveOrg(org);
                setUserRole(org.role || localStorage.getItem('userRole'));
                // Update stored org
                localStorage.setItem('activeOrg', JSON.stringify(org));
              } else if (orgs.length > 0) {
                // If stored org not found, use first available
                const firstOrg = orgs[0];
                setActiveOrg(firstOrg);
                setUserRole(firstOrg.role);
                localStorage.setItem('activeOrgId', firstOrg.id);
                localStorage.setItem('userRole', firstOrg.role);
                localStorage.setItem('activeOrg', JSON.stringify(firstOrg));
              }
            } else if (orgs.length > 0) {
              // No stored org, use first available
              const firstOrg = orgs[0];
              setActiveOrg(firstOrg);
              setUserRole(firstOrg.role);
              localStorage.setItem('activeOrgId', firstOrg.id);
              localStorage.setItem('userRole', firstOrg.role);
              localStorage.setItem('activeOrg', JSON.stringify(firstOrg));
            } else {
              // No organizations - clear active org
              setActiveOrg(null);
              setUserRole(null);
              localStorage.removeItem('activeOrgId');
              localStorage.removeItem('userRole');
              localStorage.removeItem('activeOrg');
            }
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          
          // Check if it's a 404 (user not found) or 401 (invalid token)
          if (error.response?.status === 404 || error.response?.status === 401) {
            // Stale token - user doesn't exist in DB
            console.log('User not found or token invalid - clearing session');
            authService.logout();
            toast.error('Your session has expired. Please login again.');
            navigate('/login');
          } else {
            // Other errors - still logout for safety
            authService.logout();
            navigate('/login');
          }
        }
      }
      setLoading(false);
      setInitialized(true);
    };

    initAuth();
  }, []); // Empty dependency array - only run once on mount

  const login = async (email, password) => {
    try {
      const { user, organizations } = await authService.login(email, password);
      setUser(user);
      setOrganizations(organizations || []);
      
      // Check for pending invite token
      const pendingInviteToken = localStorage.getItem('pendingInviteToken');
      if (pendingInviteToken) {
        try {
          const { inviteService } = await import('@/services/invite.service');
          const result = await inviteService.acceptInvite(pendingInviteToken);
          localStorage.removeItem('pendingInviteToken');
          toast.success(`You have been added to ${result.organization?.name || 'the organization'}!`);
          // Reload to get updated organizations
          window.location.href = '/';
          return { success: true };
        } catch (inviteErr) {
          console.error('Failed to accept invite:', inviteErr);
          localStorage.removeItem('pendingInviteToken');
          toast.error('Failed to accept invitation. Please try again from the invite link.');
          // Continue with normal login flow
        }
      }
      
      if (organizations && organizations.length > 0) {
        // Auto-select first organization
        const org = organizations[0];
        setActiveOrg(org);
        setUserRole(org.role);
        
        // If client, redirect directly to their wedding
        if (org.role === 'client' || org.role === 'read_only_client') {
          try {
            const { weddingService } = await import('@/services/wedding.service');
            const weddings = await weddingService.getMyWeddings();
            if (weddings && weddings.length > 0) {
              // Redirect directly to the wedding detail page
              window.location.href = `/wedding/${weddings[0].id}`;
              return { success: true };
            }
          } catch (error) {
            console.error('Failed to load client wedding:', error);
            // Fall through to normal redirect
          }
        }
        
        // Force cache refresh on login to ensure latest code is loaded
        // Use window.location instead of navigate to trigger full page reload
        window.location.href = '/';
      } else {
        // No organizations, go to setup
        window.location.href = '/setup-organization';
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      // Don't navigate or reload on login error - let the Login page handle the error
      throw new Error(error.response?.data?.detail || 'Login failed');
    }
  };

  const signup = async (name, email, password) => {
    try {
      const result = await authService.signup(name, email, password);

      if (result?.requires_verification) {
        localStorage.setItem('pendingVerificationEmail', result.email || email);
        // Reset any previous organization state so new users don't inherit old orgs
        setOrganizations([]);
        setActiveOrg(null);
        setUserRole(null);
        localStorage.removeItem('activeOrgId');
        localStorage.removeItem('userRole');
        navigate(`/verify-email?email=${encodeURIComponent(result.email || email)}`);
        return { success: true, requires_verification: true };
      }

      if (result?.user) {
        setUser(result.user);
      }

      // Reset any previous organization state so new users don't inherit old orgs
      setOrganizations([]);
      setActiveOrg(null);
      setUserRole(null);
      localStorage.removeItem('activeOrgId');
      localStorage.removeItem('userRole');
      
      // After signup, go to org setup
      navigate('/setup-organization');
      
      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      throw new Error(error.response?.data?.detail || 'Signup failed');
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const { user, organizations } = await authService.verifyEmail(email, code);
      if (user) {
        setUser(user);
        localStorage.setItem('user', JSON.stringify(user));
      }
      setOrganizations(organizations || []);

      const pendingInviteToken = localStorage.getItem('pendingInviteToken');
      if (pendingInviteToken) {
        try {
          const { inviteService } = await import('@/services/invite.service');
          const result = await inviteService.acceptInvite(pendingInviteToken);
          localStorage.removeItem('pendingInviteToken');
          toast.success(`You have been added to ${result.organization?.name || 'the organization'}!`);
          window.location.href = '/';
          return { success: true };
        } catch (inviteErr) {
          console.error('Failed to accept invite:', inviteErr);
          localStorage.removeItem('pendingInviteToken');
          toast.error('Failed to accept invitation. Please try again from the invite link.');
        }
      }

      if (organizations && organizations.length > 0) {
        const org = organizations[0];
        setActiveOrg(org);
        setUserRole(org.role);
        localStorage.setItem('activeOrgId', org.id);
        localStorage.setItem('userRole', org.role);
        localStorage.setItem('activeOrg', JSON.stringify(org));
        window.location.href = '/';
      } else {
        navigate('/setup-organization');
      }

      return { success: true };
    } catch (error) {
      console.error('Verify email error:', error);
      throw new Error(error.response?.data?.detail || 'Verification failed');
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setActiveOrg(null);
    setUserRole(null);
    navigate('/login');
  };

  const switchOrganization = async (orgId) => {
    try {
      const orgs = await authService.getOrganizations();
      setOrganizations(orgs);
      const org = orgs.find(o => o.id === orgId);
      
      if (org) {
        setActiveOrg(org);
        setUserRole(org.role);
        localStorage.setItem('activeOrgId', org.id);
        localStorage.setItem('userRole', org.role);
        // Store org data with credits
        localStorage.setItem('activeOrg', JSON.stringify(org));
        navigate('/');
      }
    } catch (error) {
      console.error('Switch organization error:', error);
      toast.error('Failed to switch organization');
    }
  };

  const createOrganization = async (name, currency = 'INR', timezone = 'Asia/Kolkata') => {
    try {
      const org = await authService.createOrganization(name, currency, timezone);
      
      // Reload organizations and set as active
      const orgs = await authService.getOrganizations();
      setOrganizations(orgs);
      const newOrg = orgs.find(o => o.id === org.id);
      
      if (newOrg) {
        setActiveOrg(newOrg);
        setUserRole(newOrg.role);
        localStorage.setItem('activeOrgId', newOrg.id);
        localStorage.setItem('userRole', newOrg.role);
      }
      
      // Navigate to homepage after successful creation
      navigate('/');
      
      return org;
    } catch (error) {
      console.error('Create organization error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to create organization');
    }
  };

  const getUserOrganizations = () => {
    return organizations;
  };

  const updateUser = async () => {
    if (authService.isAuthenticated()) {
      try {
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  const value = {
    user,
    activeOrg,
    userRole,
    organizations,
    loading,
    login,
    signup,
    verifyEmail,
    logout,
    switchOrganization,
    createOrganization,
    getUserOrganizations,
    updateUser,
    isAuthenticated: authService.isAuthenticated()
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
